/**
 * SchedulingTab — Slot durations, buffer, overbooking policy, multi-resource.
 *
 * Slot durations are a chip list (click to remove, input+Add to create).
 * Buffer/overbooking are simple inputs.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Plus, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../../../shared/ui/Button';
import { Card, CardBody, CardFooter, CardHeader } from '../../../shared/ui/Card';
import { FieldError } from '../../../shared/ui/FieldError';
import { Input } from '../../../shared/ui/Input';
import { Label } from '../../../shared/ui/Label';
import { Select } from '../../../shared/ui/Select';
import { Switch } from '../../../shared/ui/Switch';
import { cn } from '../../../shared/utils/cn';
import { useUpdateBusinessProfile } from '../../../features/business-profiles/useBusinessProfiles';
import {
  OverbookingPolicyEnum,
  type BusinessProfileRecord,
} from '../../../features/business-profiles/types';

const schedulingFormSchema = z.object({
  slotDurations: z.array(z.number().int().min(5).max(480)).min(1),
  bufferMinutes: z.coerce.number().int().min(0).max(120),
  overbookingPolicy: OverbookingPolicyEnum,
  multiResourceEnabled: z.boolean(),
  resources: z.array(z.string()),
});
type SchedulingFormValues = z.infer<typeof schedulingFormSchema>;

export function SchedulingTab({
  record,
}: {
  record: BusinessProfileRecord;
}): JSX.Element {
  const { t } = useTranslation('adminProfiles');
  const updateMutation = useUpdateBusinessProfile(record.subVerticalKey);
  const [justSaved, setJustSaved] = useState(false);
  const [slotInput, setSlotInput] = useState('');
  const [resourceInput, setResourceInput] = useState('');

  const defaults: SchedulingFormValues = {
    slotDurations: record.profile.scheduling.slotDurations,
    bufferMinutes: record.profile.scheduling.bufferMinutes,
    overbookingPolicy: record.profile.scheduling.overbookingPolicy,
    multiResourceEnabled: record.profile.scheduling.multiResource?.enabled ?? false,
    resources: record.profile.scheduling.multiResource?.resources ?? [],
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<SchedulingFormValues>({
    resolver: zodResolver(schedulingFormSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    reset(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id]);

  const slots = watch('slotDurations');
  const resources = watch('resources');
  const multiResourceEnabled = watch('multiResourceEnabled');

  const handleAddSlot = () => {
    const n = parseInt(slotInput, 10);
    if (!Number.isFinite(n) || n < 5 || n > 480) return;
    if (slots.includes(n)) return;
    setValue('slotDurations', [...slots, n].sort((a, b) => a - b), { shouldDirty: true });
    setSlotInput('');
  };
  const handleRemoveSlot = (n: number) => {
    if (slots.length <= 1) return;
    setValue('slotDurations', slots.filter((v) => v !== n), { shouldDirty: true });
  };

  const handleAddResource = () => {
    const r = resourceInput.trim();
    if (!r || resources.includes(r)) return;
    setValue('resources', [...resources, r], { shouldDirty: true });
    setResourceInput('');
  };
  const handleRemoveResource = (r: string) => {
    setValue('resources', resources.filter((v) => v !== r), { shouldDirty: true });
  };

  const onSubmit = async (values: SchedulingFormValues) => {
    setJustSaved(false);
    await updateMutation.mutateAsync({
      scheduling: {
        slotDurations: values.slotDurations,
        bufferMinutes: values.bufferMinutes,
        overbookingPolicy: values.overbookingPolicy,
        ...(values.multiResourceEnabled
          ? {
              multiResource: {
                enabled: true,
                resources: values.resources,
              },
            }
          : {}),
      },
    });
    reset(values);
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2500);
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <CardHeader>
          <div>
            <h2 className="text-biz-lg biz-heading font-semibold">
              {t('scheduling.title')}
            </h2>
            <p className="text-biz-sm text-biz-muted-foreground mt-biz-content">
              {t('scheduling.hint')}
            </p>
          </div>
        </CardHeader>
        <CardBody className="space-y-biz-section">
          <section>
            <Label>{t('scheduling.slotsLabel')}</Label>
            <p className="text-biz-xs text-biz-muted-foreground mt-biz-content">
              {t('scheduling.slotsHint')}
            </p>
            <ul className="flex flex-wrap gap-biz-content mt-biz-content-lg">
              {slots.map((n) => (
                <li key={n}>
                  <button
                    type="button"
                    onClick={() => handleRemoveSlot(n)}
                    aria-label={`${n}m — ${t('scheduling.removeSlot')}`}
                    disabled={slots.length <= 1}
                    className={cn(
                      'group inline-flex items-center gap-biz-content',
                      'px-biz-content-lg py-biz-content min-h-[44px]',
                      'rounded-biz-full border font-mono text-biz-sm',
                      'bg-biz-primary-subtle text-biz-primary border-biz-primary/30 hover:bg-biz-primary/10',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
                      'transition-colors duration-biz-fast',
                    )}
                  >
                    <span>{n}m</span>
                    <X size={14} weight="bold" aria-hidden="true" className="opacity-60 group-hover:opacity-100" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex gap-biz-content-lg mt-biz-content-lg flex-col md:flex-row">
              <Input
                type="number"
                min={5}
                max={480}
                placeholder="30"
                value={slotInput}
                onChange={(e) => setSlotInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSlot();
                  }
                }}
                aria-label={t('scheduling.addSlotLabel')}
                className="md:max-w-[160px]"
              />
              <Button
                type="button"
                intent="secondary"
                leftIcon={<Plus size={18} weight="regular" />}
                onClick={handleAddSlot}
              >
                {t('scheduling.addSlotCta')}
              </Button>
            </div>
            <FieldError>{errors.slotDurations?.message}</FieldError>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-biz-content-lg">
            <div>
              <Label htmlFor="bufferMinutes">{t('scheduling.bufferLabel')}</Label>
              <p className="text-biz-xs text-biz-muted-foreground mt-biz-content mb-biz-content">
                {t('scheduling.bufferHint')}
              </p>
              <Input
                id="bufferMinutes"
                type="number"
                min={0}
                max={120}
                invalid={Boolean(errors.bufferMinutes)}
                {...register('bufferMinutes')}
              />
              <FieldError>{errors.bufferMinutes?.message}</FieldError>
            </div>

            <div>
              <Label htmlFor="overbookingPolicy">
                {t('scheduling.overbookingLabel')}
              </Label>
              <p className="text-biz-xs text-biz-muted-foreground mt-biz-content mb-biz-content">
                {t('scheduling.overbookingHint')}
              </p>
              <Select id="overbookingPolicy" {...register('overbookingPolicy')}>
                {OverbookingPolicyEnum.options.map((o) => (
                  <option key={o} value={o}>
                    {t(`scheduling.overbookingOptions.${o}` as const)}
                  </option>
                ))}
              </Select>
            </div>
          </section>

          <section className="pt-biz-content-lg border-t border-biz-border">
            <div className="flex items-start justify-between gap-biz-content-lg">
              <div>
                <Label>{t('scheduling.multiResourceLabel')}</Label>
                <p className="text-biz-xs text-biz-muted-foreground mt-biz-content">
                  {t('scheduling.multiResourceHint')}
                </p>
              </div>
              <Controller
                control={control}
                name="multiResourceEnabled"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-label={t('scheduling.multiResourceLabel')}
                  />
                )}
              />
            </div>

            {multiResourceEnabled ? (
              <div className="mt-biz-content-lg">
                <ul className="flex flex-wrap gap-biz-content">
                  {resources.map((r) => (
                    <li key={r}>
                      <button
                        type="button"
                        onClick={() => handleRemoveResource(r)}
                        aria-label={`${r} — ${t('scheduling.removeResource')}`}
                        className={cn(
                          'group inline-flex items-center gap-biz-content',
                          'px-biz-content-lg py-biz-content min-h-[44px]',
                          'rounded-biz-full border text-biz-sm',
                          'bg-biz-accent-subtle text-biz-accent border-biz-accent/30 hover:bg-biz-accent/10',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
                          'transition-colors duration-biz-fast',
                        )}
                      >
                        <span>{r}</span>
                        <X size={14} weight="bold" aria-hidden="true" className="opacity-60 group-hover:opacity-100" />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-biz-content-lg mt-biz-content-lg flex-col md:flex-row">
                  <Input
                    placeholder={t('scheduling.resourcePlaceholder')}
                    value={resourceInput}
                    onChange={(e) => setResourceInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddResource();
                      }
                    }}
                    aria-label={t('scheduling.addResourceLabel')}
                  />
                  <Button
                    type="button"
                    intent="secondary"
                    leftIcon={<Plus size={18} weight="regular" />}
                    onClick={handleAddResource}
                  >
                    {t('scheduling.addResourceCta')}
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        </CardBody>
        <CardFooter>
          {justSaved ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-biz-sm text-biz-success mr-auto"
            >
              <CheckCircle weight="regular" size={18} aria-hidden="true" />
              {t('common.saved')}
            </span>
          ) : null}
          <Button
            type="button"
            intent="secondary"
            onClick={() => reset(defaults)}
            disabled={!isDirty || isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" intent="primary" loading={isSubmitting} disabled={!isDirty}>
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
